import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';

import { ChartModule } from 'primeng/chart';
import { CardModule } from 'primeng/card';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Chart } from 'chart.js';

import { DATE_FILTERS } from '@shared/contants/variables';
import { PriceMismatchService } from '@services/price-mismatch.service';
import { IPriceMismatchResponse } from '@shared/interfaces/price-mismatch.interface';
import { PageHeaderComponent } from '@shared/components/page-header/page-header.component';

@Component({
    selector: 'app-price-mismatch',
    standalone: true,
    imports: [CommonModule, ChartModule, CardModule, PageHeaderComponent],
    templateUrl: './price-mismatch.component.html',
    styleUrl: './price-mismatch.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PriceMismatchComponent implements OnInit {
    priceMismatchService = inject(PriceMismatchService);
    filterDateSearch = signal<string>('');
    beginDate = signal<string>('');
    endDate = signal<string>('');

    priceMismatchData = signal<IPriceMismatchResponse | null>(null);

    barChartData = signal<any>({
        labels: [],
        datasets: [
            {
                label: 'Match%',
                data: [],
                backgroundColor: '#4CAF50',
                barPercentage: 0.4
            },
            {
                label: 'Mismatch%',
                data: [],
                backgroundColor: '#D32F2F',
                barPercentage: 0.4
            }
        ]
    });

    barChartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
                align: 'end',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    font: { size: 12 }
                }
            },
            datalabels: {
                anchor: 'center',
                align: 'center',
                color: '#ffffff',
                font: { weight: 'bold', size: 12 },
                formatter: (value: number) => ` %${Math.round(value * 100) / 100}`
            } as any
        },
        scales: {
            x: {
                title: { display: true, text: 'Agent Name', font: { size: 14, weight: 'bold' } },
                stacked: true,
                grid: { display: false }
            },
            y: {
                title: { display: true, text: 'Book Request Ratio (%)', font: { size: 14, weight: 'bold' } },
                stacked: true,
                beginAtZero: true,
                max: 100
            }
        }
    };

    pieChartData = signal<any>({
        labels: ['Price Match', 'Price Mismatch'],
        datasets: [
            {
                data: [],
                backgroundColor: ['#4CAF50', '#D32F2F']
            }
        ]
    });

    pieChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                align: 'center',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    font: { size: 12 }
                }
            },
            datalabels: false
        }
    };

    constructor() {
        Chart.register(ChartDataLabels);
    }

    ngOnInit() {
        this.filterDateSearch.set(sessionStorage.getItem(DATE_FILTERS.FLIGHT_DATE_SEARCH) || '');
        this.beginDate.set(sessionStorage.getItem(DATE_FILTERS.BEGIN_DATE) || '');
        this.endDate.set(sessionStorage.getItem(DATE_FILTERS.END_DATE) || '');

        this.getPriceMismatch();
    }

    getPriceMismatch() {
        this.priceMismatchService.getPriceMismatch(this.beginDate(), this.endDate()).subscribe({
            next: (response: any) => {
                this.priceMismatchData.set(response);
                this.setBarChartData(response);
                this.setPieChartData(response);
            },
            error: (error) => {
                console.error(error);
            }
        });
    }

    setBarChartData(response: any) {
        const labels = Object.keys(response.matchRatio);
        const matchData = Object.values(response.matchRatio) as number[];
        const mismatchData = matchData.map((value) => 100 - value);

        this.barChartData.set({
            ...this.barChartData(),
            labels,
            datasets: this.barChartData().datasets.map((dataset: any) => ({
                ...dataset,
                data: dataset.label === 'Match%' ? matchData : mismatchData
            }))
        });
    }

    setPieChartData(response: any) {
        const match = response.priceMatchNumber;
        const mismatch = response.priceMismatchNumber;

        this.pieChartData.set({
            ...this.pieChartData(),
            datasets: [
                {
                    ...this.pieChartData().datasets[0],
                    data: [match, mismatch]
                }
            ]
        });
    }
}
